export const TITLE_CASE_EXCEPTIONS: readonly string[] = [
  'a',
  'for',
  'so',
  'an',
  'in',
  'the',
  'and',
  'nor',
  'to',
  'at',
  'of',
  'up',
  'but',
  'on',
  'yet',
  'by',
  'or',
];

/**
 * Sentence cases a string.
 * @example 'it was a hot sunny day' => 'It was a hot sunny day'
 */
export const sentenceCase = (input: string): string => {
  // If input is null, undefined, or empty string return it as is.
  if (!input) return input;

  // We need to trim so we can ensure the first character gets uppercased.
  const formattedString = input.trim();

  return (
    formattedString.charAt(0).toUpperCase() +
    formattedString.toLowerCase().slice(1)
  );
};

/**
 * Start cases a string.
 * @example 'the cow jumped over the moon for fun' => 'The Cow Jumped Over The Moon For Fun'
 */
export const startCase = (input: string): string => {
  // If input is null, undefined, or empty string return it as is.
  if (!input) return input;

  // 1. Cleanup the string and lowercase.
  const formattedString = input
    .trim()
    .toLowerCase()

    // 2. Split the string into an array of strings.
    .split(' ')

    // 3. Map over the array
    .map((word) => word.replace(word.charAt(0), word.charAt(0).toUpperCase()));

  return formattedString.join(' ');
};

/**
 * Title cases a string. Ignores articles, conjunctions, and prepositions.
 * @example 'the art Of coding' => 'The Art of Coding'
 */
export const titleCase = (input: string): string => {
  // If input is null, undefined, or empty string return it as is.
  if (!input) return input;

  // 1. First lets startCase the string.
  const words = startCase(input)
    // 2. TODO: Find all the words that shouldn't be capitalized and lowercase them (ie. articles, conjunctions, and prepositions).
    .split(' ');

  return words
    .map((word, index) => {
      if (index === 0) return word;

      // Last word is always capitalized.
      if (index === words.length - 1) return word;

      return TITLE_CASE_EXCEPTIONS.includes(word.toLowerCase())
        ? word.toLowerCase()
        : word;
    })
    .join(' ');
};

/**
 * Pascal cases a string.
 * @example 'a blue bunny' => 'ABlueBunny'
 */
export const pascalCase = (input: string): string => {
  // If input is null, undefined, or empty string return it as is.
  if (!input) return input;

  // 1. First lets startCase the string.
  const formattedString = startCase(input)
    // 2. Split the string into the array of strings
    .split(' ')

    // 3. Join the words with no space
    .join('');

  return formattedString;
};

/**
 * Camel cases a string.
 * @example 'a blue bunny' => 'aBlueBunny'
 */
export const camelCase = (input: string): string => {
  // If input is null, undefined, or empty string return it as is.
  if (!input) return input;

  // 1. First lets pascalCase the string.
  const formattedString = pascalCase(input);

  // 2. Lowercase the first character
  return formattedString.charAt(0).toLowerCase() + formattedString.slice(1);
};

/**
 * Hyphen cases a string.
 * @example 'a blue bunny' => 'a-blue-bunny'
 */
export const hyphenCase = (input: string): string => {
  // If input is null, undefined, or empty string return it as is.
  if (!input) return input;

  // 1. Cleanup the string and lowercase
  const formattedString = input
    .trim()
    .toLowerCase()

    // 2. Split the string into array of strings
    .split(' ')

    // 3. Join with hyphens
    .join('-');

  return formattedString;
};

/**
 * Snake cases a string.
 * @example 'a blue bunny' => 'a_blue_bunny'
 */
export const snakeCase = (input: string): string => {
  // If input is null, undefined, or empty string return it as is.
  if (!input) return input;

  // 1. Cleanup the string and lowercase
  const formattedString = input
    .trim()
    .toLowerCase()

    // 2. Split the string into array of strings
    .split(' ')

    // 3. Join with underscores
    .join('_');

  return formattedString;
};

/**
 * Dot cases a string.
 * @example 'a blue bunny' => 'a.blue.bunny'
 */
export const dotCase = (input: string): string => {
  // If input is null, undefined, or empty string return it as is.
  if (!input) return input;

  // 1. Cleanup the string and lowercase
  const formattedString = input
    .trim()
    .toLowerCase()

    // 2. Split the string into array of strings
    .split(' ')

    // 3. Join with dots
    .join('.');

  return formattedString;
};

/**
 * Replaces hyphens with spaces. Useful to make API parameterized strings
 * readable to the end user.
 * @example 'refund-date' => 'refund date'
 * @example 'refund-date - something' => 'refund date - something'
 */
export const deparameterize = (input: string): string => {
  // If input is null, undefined return empty string.
  if (input === undefined || input === null) return '';

  let formattedString = input.toString();

  // Replace underscores with spaces
  formattedString = formattedString.replaceAll('_', ' ');

  // Replace hyphens with spaces, if surrounded with characters
  // so, 'refund-date' => 'refund date'
  // but, 'refund-date - something' => 'refund date - something'
  formattedString = formattedString.replaceAll(/\b-\b/g, ' ');

  // Squish whitespace
  formattedString = formattedString.replaceAll(/\s+/g, ' ');
  formattedString = formattedString.trim();

  return formattedString;
};

/**
 * A quick helper to deparameterize a string and sentence case it.
 * @example 'refund-date' => 'Refund date'
 */
export const sentencize = (input: string): string => {
  // If input is null, undefined return empty string.
  if (input === undefined || input === null) return '';

  return sentenceCase(deparameterize(input.toString()));
};

/**
 * A quick helper to turn a noun into a possesive noun
 * @example 'Juls' => 'Juls''
 * @example 'Julio' => 'Julio's'
 */
export const possessive = (input: string): string => {
  // If input is null, undefined return empty string.
  if (input === undefined || input === null) return '';

  const formattedString = input.toString();

  return formattedString.endsWith('s')
    ? `${formattedString}'`
    : `${formattedString}'s`;
};
